import { useState } from "react";
import { Button, Box, TextField } from "@mui/material";
import {
  addButtonPadding,
  DeletePatientButton,
  editPatientButton,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import Grid from "@mui/material/Grid";
import "../../common/Common.css";
import { DataGrid } from "@mui/x-data-grid";
import ServiceCreationForm from "./ServiceCreationForm";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import validators from "../../utils/validations/validators";
import api from "../../services/api";

export default function ServiceTable(props) {
  const { branchId, getServices, rows } = props;
  const [open, setOpen] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [serviceID, setServiceID] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({});
  const handleOpen = () => setOpen(true);
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown") {
      return;
    }
    setFormData({});
      setValidationErrors({});
    setOpen(false);
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "consultation_fee",
      headerName: "Consultation Fee",
      type: "number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "therapy_session_fee",
      headerName: "Therapy Session Fee",
      type: "number",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "therapy_duration_minutes",
      headerName: "Therapy Duration Minutes",
      type: "number",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            gap: "8px",
          }}
        >
          <BorderColorIcon
            sx={editPatientButton}
            onClick={() => {
              handleServiceEdit(params.row.id);
            }}
          />
          <DeleteIcon
            sx={DeletePatientButton}
            onClick={() => {
              handleServiceDelete(params.row.id);
            }}
          />
        </div>
      ),
    },
  ];

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Validate live
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const requiredFields = [
    "name",
    "description",
    "consultation_fee",
    "therapy_session_fee",
    "therapy_duration_minutes",
  ];

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    requiredFields.every(
      (field) =>
        formData[field] !== "" &&
        formData[field] !== null &&
        formData[field] !== undefined
    );

  const handleCreateService = async () => {
    if(!isFormValid){
      setSnackbarOpen({
        status: true,   
        message: "Please fill all required fields correctly.",
        severity: "error",
      });
      return;
    }
    if (isEditClicked) {
      sendServiceEdit();
    } else {
      const updatedFormData = {
        ...formData,
        branch_id: branchId,
      };
      try {
        const response = await api.post(
          `app/management/therapy-service/`,
          updatedFormData,
        );
        if (response.status === 200) {
          setFormData({});
          setSnackbarOpen({
            status: true,
            message: "Service added successfully!",
          });
          handleClose();
          getServices();
        } else {
          setSnackbarOpen({
            status: true,
            message: "Something went wrong. Please try again later.",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    }
  };

  const handleServiceEdit = async (id) => {
    setServiceID(id);
    setIsEditClicked(true);
    await getDetailsServiceWise(id);
    handleOpen();
  };

  const getDetailsServiceWise = async (id) => {
    try {
      let serviceData = await api.get(
        `app/management/therapy-service/${id}`,
      );
      if (serviceData?.status === 200) {
        setFormData(serviceData?.data);
      } else {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const sendServiceEdit = async () => {
    try {
      let updatedServiceData = await api.put(
        `app/management/therapy-service/${serviceID}`,
        formData,
      );
      if (updatedServiceData?.status === 200) {
        setFormData({});
        setIsEditClicked(false);
        setSnackbarOpen({
          status: true,
          message: "Service Updated successfully!",
        });
        handleClose();
        getServices();
      } else {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const handleServiceDelete = async (id) => {
    try {
      let deletedServiceData = await api.delete(
        `app/management/therapy-service/${id}`,
      );
      if (deletedServiceData?.status === 200) {
        setSnackbarOpen({
          status: true,
          message: "Service Deleted successfully!",
        });
        getServices();
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const handleSnackClose = () => setSnackbarOpen(false);
  return (
    <>
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
      <Box className="tabcontent_box">
        <Grid
          container
          spacing={2}
          className="patientTable-btnstack"
          display="flex"
          alignItems="center"
        >
          <Grid size={8}>
            <Button
              variant="contained"
              sx={[registerPatientButtonStyles,addButtonPadding]}
              size="small"
              onClick={handleOpen}
            >
              Add New Service
            </Button>
          </Grid>
          <Grid size={4} sx={{ textAlign: "right" }}>
            <TextField
              size="small"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
        </Grid>
        <DataGrid
          sx={{
            fontFamily: "Nunito, sans-serif",
            // Cell (row content) font size
            "& .MuiDataGrid-cell": {
              fontSize: "12px",
            },      
            // Header font size
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "13px",
              fontWeight: "bold",
            },
          }}
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
          isCellEditable={() => false}
        />
      </Box>
      <ServiceCreationForm
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleCreateService={handleCreateService}
        isEditClicked={isEditClicked}
        isFormValid={isFormValid}
        validationErrors={validationErrors}
      />
    </>
  );
}
